import { Link } from 'react-router-dom'
import { BookOpen, ListTree } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReaderToc, ReaderTocChapter } from '@/api/readerApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { formatCount } from '@/utils/format'

interface TocDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  toc?: ReaderToc
  currentChapterNo?: number | null
  readingProgress: number
  isMobile: boolean
}

const MODALITY_BADGES: Array<{ key: keyof ReaderTocChapter['modalities']; icon: string; labelKey: string }> = [
  { key: 'text', icon: '📖', labelKey: 'reader.modalities.text' },
  { key: 'image', icon: '🎨', labelKey: 'reader.modalities.image' },
  { key: 'audio', icon: '🔊', labelKey: 'reader.modalities.audio' },
  { key: 'video', icon: '🎬', labelKey: 'reader.modalities.video' },
]

export function TocDrawer({ open, onOpenChange, projectId, toc, currentChapterNo, readingProgress, isMobile }: TocDrawerProps) {
  const { t } = useTranslation()
  const chapters = toc?.chapters ?? []
  const currentIndex = chapters.findIndex((chapter) => chapter.chapter_no === currentChapterNo)
  const chapterProgress = currentIndex >= 0 && chapters.length > 0 ? ((currentIndex + 1) / chapters.length) * 100 : 0

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={isMobile ? 'bottom' : 'left'} className={cn('gap-0 p-0', isMobile ? 'max-h-[82vh]' : 'w-[380px] sm:max-w-[380px]')}>
        <SheetHeader className="border-b p-4">
          <div className="flex items-center gap-2">
            <ListTree className="size-4 text-primary" />
            <SheetTitle>{t('reader.toc')}</SheetTitle>
          </div>
          <SheetDescription>
            {toc?.project.title ? t('reader.tocDescription', { title: toc.project.title }) : t('reader.tocLoading')}
          </SheetDescription>
        </SheetHeader>

        <div className="border-b p-4">
          <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('reader.chapterProgress')}</span>
            <span>{formatCount(Math.round(chapterProgress))}%</span>
          </div>
          <Progress value={chapterProgress} />
          {currentChapterNo ? (
            <div className="mt-3">
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{t('reader.readingProgress')}</span>
                <span>{formatCount(Math.round(readingProgress))}%</span>
              </div>
              <Progress value={readingProgress} />
            </div>
          ) : null}
        </div>

        <ScrollArea className={isMobile ? 'h-[54vh]' : 'h-[calc(100vh-13rem)]'}>
          <div className="space-y-1 p-3">
            {chapters.length === 0 ? (
              <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">{t('reader.emptyToc')}</div>
            ) : (
              chapters.map((chapter) => {
                const active = chapter.chapter_no === currentChapterNo
                return (
                  <Button
                    key={chapter.chapter_no}
                    asChild
                    variant={active ? 'secondary' : 'ghost'}
                    className="h-auto w-full justify-start px-3 py-2 text-left"
                    onClick={() => onOpenChange(false)}
                  >
                    <Link to={`/projects/${encodeURIComponent(projectId)}/reader/ch/${chapter.chapter_no}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <BookOpen className={cn('size-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
                          <span className="truncate font-medium">
                            {t('reader.chapterHeading', { number: formatCount(chapter.chapter_no), title: chapter.title || t('reader.untitled') })}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-6">
                          <span className="text-xs text-muted-foreground">{t('reader.wordCount', { value: formatCount(chapter.word_count ?? 0) })}</span>
                          {MODALITY_BADGES.filter((item) => chapter.modalities[item.key]).map((item) => (
                            <Badge key={item.key} variant="outline" className="h-5 px-1.5 text-[11px]" title={t(item.labelKey)}>
                              <span aria-hidden>{item.icon}</span>
                              <span className="sr-only">{t(item.labelKey)}</span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Link>
                  </Button>
                )
              })
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
